<script lang="ts">
  import type { TooltipData } from "$lib/stores/tooltipState.svelte";
  import { clampTooltipToViewport } from "$lib/utils/tooltipPosition";

  interface Props {
    x?: number;
    y?: number;
    content?: string | TooltipData;
  }

  let { x = 0, y = 0, content = "" }: Props = $props();

  /**
   * Останній компонент проєкту на `export let` — і лишався він не з недогляду.
   * Позиція тут не обчислюється зі стану, а виводиться з ВИМІРУ DOM
   * (`offsetWidth` після рендера), тож прямолінійний переклад дав би `$effect`
   * із записом у `$state` — сам по собі HIGH за SVELTE-CORE-v8 § 1.2.
   *
   * `bind:offsetWidth`/`bind:offsetHeight` знімають дилему цілком: вимір стає
   * реактивним значенням, яке веде сам Svelte (через ResizeObserver), а позиція
   * — звичайним `$derived` від нього. Жодного `$effect` і жодного запису в стан
   * із побічного ефекту.
   *
   * Нулі до першого виміру — не проблема й більше не потребують
   * `eslint-disable`: `clampTooltipToViewport` віддає координати як є, доки
   * розмір нульовий.
   */
  let measuredWidth = $state(0);
  let measuredHeight = $state(0);

  const position = $derived(
    clampTooltipToViewport({
      x,
      y,
      width: measuredWidth,
      height: measuredHeight,
      viewportWidth: typeof window === "undefined" ? 0 : window.innerWidth,
      viewportHeight: typeof window === "undefined" ? 0 : window.innerHeight,
    }),
  );

  function isStructured(data: unknown): data is TooltipData {
    return typeof data === "object" && data !== null && "hotkeys" in data;
  }
</script>

<div
  class="tooltip"
  style="left: {position.x}px; top: {position.y}px;"
  bind:offsetWidth={measuredWidth}
  bind:offsetHeight={measuredHeight}
>
  {#if isStructured(content)}
    {#if content.title}
      <div class="tooltip-title">{content.title}</div>
    {/if}

    {#if content.title && content.hotkeys && content.hotkeys.length > 0}
      <hr class="tooltip-divider" />
    {/if}

    {#if content.hotkeys && content.hotkeys.length > 0}
      <div class="hotkey-title">HotKey</div>
      {#each content.hotkeys as hotkey (hotkey.text)}
        <div class="hotkey-item">
          <span class="hotkey-kbd" class:single-char={hotkey.singleChar}>
            {hotkey.text}
          </span>
        </div>
      {/each}
    {/if}
  {:else}
    {content}
  {/if}
</div>

<style>
  :global(.tooltip-title) {
    font-weight: bold;
    margin-bottom: 6px; /* Add some space below the title */
    text-align: center;
  }
  :global(.tooltip-divider) {
    border: none;
    border-top: 1px solid var(--border-color, #444);
    margin: 6px 0; /* Add space around the divider */
  }
  :global(.hotkey-title) {
    font-weight: bold;
    margin-bottom: 4px;
    text-align: center;
  }
  :global(.hotkey-item) {
    display: flex;
    justify-content: center;
  }
  :global(.hotkey-item:not(:last-child)) {
    margin-bottom: 4px;
  }
  :global(.hotkey-kbd) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    padding: 0 6px;
    border: var(--global-border-width) solid var(--border-color, #444);
    border-radius: 4px;
    background: var(--bg-primary, #1e1e1e);
    font-weight: bold;
  }
  :global(.hotkey-kbd.single-char) {
    width: 20px;
    padding: 0;
  }
  .tooltip {
    position: fixed;
    padding: 8px 12px;
    background: var(--bg-secondary, #2a2a2a);
    color: var(--text-primary, #fff);
    border-radius: var(--unified-border-radius, 12px);
    border: var(--unified-border, 1px solid rgba(255, 255, 255, 0.1));
    box-shadow: var(--unified-shadow, 0 4px 12px rgba(0, 0, 0, 0.2));
    backdrop-filter: var(--unified-backdrop-filter, blur(10px));
    z-index: 10001; /* Higher than modal overlay */
    pointer-events: none; /* Tooltip should not be interactive */
    font-size: 0.9em;
    white-space: pre-line;
    opacity: 0;
    transform: translateY(5px);
    animation: fadeIn 0.2s ease-out forwards;
  }

  @keyframes fadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
